import React, { useState, useMemo } from 'react';

import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';

import { Trash2, Flame, ArrowDownRight, TrendingDown, Home, Percent, Calendar, CheckCircle, Trophy, RotateCcw, Brain, Zap } from 'lucide-react';

import { Card } from '../ui/Card';

import { InputGroup } from '../ui/InputGroup';

import { SectionHeader } from '../ui/SectionHeader';

import { MasterTipsCard } from '../ui/MasterTipsCard';

import { formatCurrency } from '../../utils/finance';

import { LIABILITY_CATEGORIES } from '../../utils/constants';



// --- HELPER: Safe Number Parsing ---

const safeNum = (val) => {

    const num = parseFloat(val);

    return isNaN(num) ? 0 : num;

};



export const DebtTab = ({ data, actions, calculations }) => {

    const { surplusCalculations } = calculations;

    

    // UI State

    const [strategy, setStrategy] = useState('avalanche'); 

    const [monthlyExtraInput, setMonthlyExtraInput] = useState(0); 

    const [extraFreq, setExtraFreq] = useState('monthly'); 



    // Normalize Extra Payment to Monthly for internal calculation logic ONLY

    const normalizedMonthlyExtra = useMemo(() => {

        const input = safeNum(monthlyExtraInput);

        if (extraFreq === 'monthly') return input;

        return (input * 26) / 12;

    }, [monthlyExtraInput, extraFreq]);



    // --- TRIGGER BADGE ON INTERACTION ---

    const handleStrategyChange = (newStrategy) => {

        setStrategy(newStrategy);

        // "Smoke Signal": Ran a debt payoff simulation

        if (actions.recordEvent) {

            actions.recordEvent('hasSimulatedDebt');

        }

    };



    // --- MATH ENGINE ---

    const calculatePayoff = (debtsToSimulate, monthlyExtraAllocation) => {

        // Filter out "Paid Off" debts from the simulation

        let currentDebts = debtsToSimulate

            .filter(d => !d.isPaidOff && safeNum(d.balance) > 0)

            .map(d => ({ 

                ...d, 

                currentBal: safeNum(d.balance), 

                rate: safeNum(d.rate),

                minPay: safeNum(d.minPayment),

                promoRate: safeNum(d.promoRate),

                promoEndDate: d.promoEndDate ? new Date(d.promoEndDate) : null

            }));



        let totalInterest = 0; 

        let months = 0;

        const maxMonths = 600; // Cap at 50 years

        

        const getRate = (d, currentDate) => {

            if (d.promoEndDate && currentDate < d.promoEndDate) {

                return d.promoRate !== undefined ? d.promoRate : d.rate;

            }

            return d.rate;

        };



        const startDate = new Date();



        while (currentDebts.some(d => d.currentBal > 0.01) && months < maxMonths) { 

            months++;

            const currentDate = new Date(startDate);

            currentDate.setMonth(startDate.getMonth() + months);

            

            // Accrue Interest

            currentDebts.forEach(d => {

                if (d.currentBal > 0) {

                    const r = getRate(d, currentDate);

                    const interest = d.currentBal * ((r / 100) / 12);

                    d.currentBal += interest;

                    totalInterest += interest;

                }

            });



            // Pay Minimums

            let availableForSnowball = monthlyExtraAllocation;

            

            currentDebts.forEach(d => {

                if (d.currentBal > 0) {

                    const pay = Math.min(d.currentBal, d.minPay);

                    d.currentBal -= pay;

                } else {

                    availableForSnowball += d.minPay;

                }

            });

            

            // Apply Snowball/Avalanche

            const activeDebts = currentDebts.filter(d => d.currentBal > 0.01);

            

            if (activeDebts.length > 0 && availableForSnowball > 0) {

                if (strategy === 'avalanche') activeDebts.sort((a, b) => getRate(b, currentDate) - getRate(a, currentDate));

                else activeDebts.sort((a, b) => a.currentBal - b.currentBal);



                let target = activeDebts[0];

                const payment = Math.min(target.currentBal, availableForSnowball);

                target.currentBal -= payment;

            }

        }

        return { totalInterest, months };

    };



    // Run Global Simulation

    const simulation = useMemo(() => {

        if (!data.debts || data.debts.length === 0) return { totalInterest: 0, months: 0 };

        return calculatePayoff(data.debts, normalizedMonthlyExtra);

    }, [data.debts, normalizedMonthlyExtra, strategy]);



    // Single Debt Chart & Date Generator

    const getSingleDebtCurve = (debt) => {

        if (debt.isPaidOff) return { dataPoints: [], stdMonths: 0, accMonths: 0 };



        const dataPoints = [];

        let month = 0;

        let balance = safeNum(debt.balance);

        let accBalance = safeNum(debt.balance);

        const rate = safeNum(debt.rate);

        const minPay = safeNum(debt.minPayment);

        const promoRate = safeNum(debt.promoRate);

        const promoEnd = debt.promoEndDate ? new Date(debt.promoEndDate) : null;

        

        if (balance <= 0) return { dataPoints: [], stdMonths: 0, accMonths: 0 };



        let stdMonths = 0;

        let accMonths = 0;

        const startSim = new Date();



        let maxMortgageMonths = 360;

        if (debt.type === 'Mortgage' && debt.startDate) {

            const start = new Date(debt.startDate);

            const now = new Date();

            const elapsed = (now - start) / (1000 * 60 * 60 * 24 * 30.44); 

            const totalTerm = (debt.originalTerm || 30) * 12;

            maxMortgageMonths = Math.max(0, totalTerm - elapsed);

        }



        while (month < 360 && (balance > 0 || accBalance > 0)) {

            const currentDate = new Date(startSim);

            currentDate.setMonth(startSim.getMonth() + month);

            

            const currentRate = (promoEnd && currentDate < promoEnd) ? promoRate : rate;



            // Standard Path

            if (balance > 0) {

                if (debt.type === 'Mortgage' && month >= maxMortgageMonths) {

                    balance = 0; 

                } else {

                    const interest = balance * ((currentRate / 100) / 12);

                    if (minPay <= interest && month > 0 && debt.type !== 'Mortgage') {

                        balance = 0; 

                    } else {

                        balance = balance + interest - minPay;

                    }

                    if (balance < 0) balance = 0;

                }

                if (balance > 0) stdMonths = month + 1;

            }



            // Accelerated Path

            if (accBalance > 0) {

                const interest = accBalance * ((currentRate / 100) / 12);

                accBalance = accBalance + interest - (minPay + normalizedMonthlyExtra);

                if (accBalance < 0) accBalance = 0;

                if (accBalance > 0) accMonths = month + 1;

            }

            

            if (month % 6 === 0 || balance === 0 || accBalance === 0) {

                dataPoints.push({ 

                    name: month, 

                    Standard: Math.round(balance), 

                    Accelerated: Math.round(accBalance) 

                });

            }

            month++;

        }

        return { dataPoints, stdMonths, accMonths };

    };



    const formatFutureDate = (months) => {

        if (!months || months <= 0) return "Paid Off";

        if (months >= 600) return "Never";

        const d = new Date();

        d.setMonth(d.getMonth() + months);

        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

    };



    const handleCommitToBurn = () => {

        if (monthlyExtraInput <= 0) return;

        actions.addArrayItem('expenseItems', {

            id: Date.now(),

            name: "Debt Accelerator (Extra)",

            amount: monthlyExtraInput,

            frequency: extraFreq,        

            dayDue: 1,

            type: 'Fixed',

            linkedDebtId: 999 

        });

        alert("Added to Burn tab! Your surplus and flow will update automatically.");

    };



    const handleExtinguish = (debt) => {

        const confirmMsg = `CONGRATULATIONS!\n\nAre you ready to mark '${debt.name}' as fully paid off?\n\nThis will set the balance to $0 and lock the card as a trophy. You can undo this later if it was a mistake.`;

        if (window.confirm(confirmMsg)) {

            actions.updateArrayItem('debts', debt.id, 'balance', 0);

            actions.updateArrayItem('debts', debt.id, 'isPaidOff', true);

        }

    };



    const handleResurrect = (debt) => {

        if (window.confirm(`Undo the 'Paid Off' status for '${debt.name}'?`)) {

            actions.updateArrayItem('debts', debt.id, 'isPaidOff', false);

        }

    };



    const surplus = surplusCalculations ? (surplusCalculations.operational || 0) : 0;

    const remainingSurplus = Math.max(0, surplus - normalizedMonthlyExtra);

    const displayDebts = data.debts || [];



    return (

        <div className="animate-fade-in">

            

            {/* --- MASTER TIPS CARD (Replaces Header & TabInfo) --- */}

            <MasterTipsCard 

                title="The Extinguisher" 

                subtitle="Contain the spread. Extinguish liabilities." 

                element="fire"

            >

                Fire represents urgent action. List your liabilities here. 

                Use the <strong>Extinguisher Calculator</strong> below to see how adding extra payments 

                (via the Snowball or Avalanche method) can free you years earlier.

            </MasterTipsCard>



            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

                {/* 1. EXTINGUISHER CARD */}

                <Card element="fire" className="bg-slate-900/50 border-orange-500/20">

                    <SectionHeader icon={Flame} title="The Extinguisher" subtitle="Cash Available from Water Tab" element="fire" />

                    <div className="space-y-4">

                        <div className="flex justify-between items-center p-3 bg-slate-950 rounded border border-slate-800">

                            <span className="text-xs text-slate-400">Total Monthly Surplus</span>

                            <span className="text-emerald-400 font-bold">{formatCurrency(surplus)}</span>

                        </div>

                        

                        <div className="p-3 bg-slate-950 rounded border border-slate-800 space-y-2">

                            <label className="text-[10px] uppercase text-slate-500 font-bold">Allocated to Debt (Extra)</label>

                            <div className="flex items-center gap-2">

                                <div className="relative flex-1">

                                    <span className="absolute left-2 top-1.5 text-slate-500 text-xs">$</span>

                                    <input 

                                        type="number" 

                                        value={monthlyExtraInput} 

                                        onChange={(e) => setMonthlyExtraInput(parseFloat(e.target.value) || 0)} 

                                        className="w-full bg-slate-900 border border-slate-700 rounded py-1 pl-5 pr-2 text-white text-sm focus:border-orange-500 outline-none"

                                    />

                                </div>

                                <select 

                                    value={extraFreq} 

                                    onChange={(e) => setExtraFreq(e.target.value)}

                                    className="bg-slate-900 border border-slate-700 rounded py-1 px-2 text-xs text-slate-300 outline-none"

                                >

                                    <option value="monthly">/ Month</option>

                                    <option value="bi-weekly">/ Paycheck</option>

                                </select>

                            </div>

                            {extraFreq === 'bi-weekly' && <p className="text-[10px] text-slate-500 text-right">= {formatCurrency(normalizedMonthlyExtra)} / month</p>}

                            

                            {monthlyExtraInput > 0 && (

                                <button onClick={handleCommitToBurn} className="w-full text-xs bg-orange-600 hover:bg-orange-500 text-white font-bold py-2 rounded transition-colors flex items-center justify-center gap-2">

                                    <Flame className="w-3 h-3" /> Commit to Burn

                                </button>

                            )}

                        </div>



                        <div className="flex justify-between items-center pt-2 border-t border-slate-800">

                            <span className="text-xs text-slate-400">Remaining Surplus</span>

                            <span className={`font-bold ${remainingSurplus < 0 ? 'text-red-400' : 'text-blue-400'}`}>{formatCurrency(remainingSurplus)}</span>

                        </div>

                        {remainingSurplus < 0 && <p className="text-[10px] text-red-400 text-center">Warning: You are allocating more than you have.</p>}

                    </div>

                </Card>



                {/* 2. STRATEGY SELECTOR & RESULTS */}

                <Card element="fire" className="lg:col-span-2 flex flex-col justify-between">

                      <div className="flex justify-between items-start mb-4">

                        <div>

                            <h3 className="font-bold text-white text-lg">Payoff Strategy</h3>

                            <p className="text-xs text-slate-400">Choose how to attack the flames.</p>

                        </div>

                        <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">

                             <button onClick={() => handleStrategyChange('avalanche')} className={`px-4 py-2 text-xs font-bold rounded flex items-center gap-2 transition-all ${strategy === 'avalanche' ? 'bg-orange-500 text-slate-900 shadow-lg' : 'text-slate-500 hover:text-white'}`}>

                                <TrendingDown className="w-3 h-3" /> Avalanche (High Interest)

                             </button>

                             <button onClick={() => handleStrategyChange('snowball')} className={`px-4 py-2 text-xs font-bold rounded flex items-center gap-2 transition-all ${strategy === 'snowball' ? 'bg-orange-500 text-slate-900 shadow-lg' : 'text-slate-500 hover:text-white'}`}>

                                <ArrowDownRight className="w-3 h-3" /> Snowball (Smallest Balance)

                             </button>

                        </div>

                      </div>



                      <div className="grid grid-cols-2 gap-4 bg-slate-950/50 p-4 rounded-xl border border-slate-800">

                        <div className="text-center p-2">

                            <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Debt Free Date</p>

                            <p className="text-2xl font-bold text-white">{formatFutureDate(simulation.months)}</p>

                            <p className="text-xs text-orange-400">{(simulation.months/12).toFixed(1)} Years</p>

                        </div>

                        <div className="text-center p-2 border-l border-slate-800">

                            <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Total Interest Paid</p>

                            <p className="text-2xl font-bold text-red-400">{formatCurrency(simulation.totalInterest)}</p>

                        </div>

                      </div>

                      

                      {/* --- NEW STRATEGY INTEL (With Inline SVGs) --- */}

                      <div className="mt-4 p-4 rounded-xl bg-slate-950/50 border border-slate-800 relative overflow-hidden animate-fade-in">

                          {strategy === 'avalanche' ? (

                              <div className="flex gap-4">

                                  <div className="p-3 bg-blue-500/10 rounded-full h-fit border border-blue-500/20">

                                      <Brain className="w-6 h-6 text-blue-400" />

                                  </div>

                                  <div className="flex-1">

                                      <h4 className="text-sm font-bold text-blue-300">The Mathematician's Choice</h4>

                                      

                                      {/* AVALANCHE SVG GRAPH */}

                                      <div className="my-2 h-16 w-full max-w-[200px] flex items-end justify-between px-2 pb-1 border-b border-slate-700">

                                          <div className="w-4 bg-orange-500 h-[80%] rounded-t-sm animate-pulse" title="High Rate (Target)"></div>

                                          <div className="w-4 bg-slate-700 h-[60%] rounded-t-sm"></div>

                                          <div className="w-4 bg-slate-700 h-[40%] rounded-t-sm"></div>

                                          <div className="w-4 bg-slate-700 h-[20%] rounded-t-sm"></div>

                                      </div>

                                      

                                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">

                                          Targeting the <strong>Highest Interest Rate</strong> first is mathematically optimal. By destroying expensive debt, you stop compound interest from growing against you. It saves the maximum amount of money in the long run.

                                      </p>

                                  </div>

                              </div>

                          ) : (

                              <div className="flex gap-4">

                                  <div className="p-3 bg-orange-500/10 rounded-full h-fit border border-orange-500/20">

                                      <Zap className="w-6 h-6 text-orange-400" />

                                  </div>

                                  <div className="flex-1">

                                      <h4 className="text-sm font-bold text-orange-300">The Behaviorist's Choice</h4>

                                      

                                      {/* SNOWBALL SVG GRAPH */}

                                      <div className="my-2 h-16 w-full max-w-[200px] flex items-end justify-between px-2 pb-1 border-b border-slate-700">

                                          <div className="w-4 bg-orange-500 h-[20%] rounded-t-sm animate-pulse" title="Low Balance (Target)"></div>

                                          <div className="w-4 bg-slate-700 h-[40%] rounded-t-sm"></div>

                                          <div className="w-4 bg-slate-700 h-[60%] rounded-t-sm"></div>

                                          <div className="w-4 bg-slate-700 h-[80%] rounded-t-sm"></div>

                                      </div>



                                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">

                                          Targeting the <strong>Smallest Balance</strong> first creates momentum. By clearing small debts quickly, you eliminate entire monthly payments, freeing up cash flow. These "quick wins" keep you motivated to extinguish the fire.

                                      </p>

                                  </div>

                              </div>

                          )}

                      </div>

                </Card>

            </div>



            {/* DEBT LIST */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {[...displayDebts].sort((a,b) => {

                    if (a.isPaidOff && !b.isPaidOff) return 1;

                    if (!a.isPaidOff && b.isPaidOff) return -1;

                    return strategy === 'avalanche' ? safeNum(b.rate) - safeNum(a.rate) : safeNum(a.balance) - safeNum(b.balance);

                }).map((debt, index) => {

                    const { dataPoints, stdMonths, accMonths } = getSingleDebtCurve(debt);

                    const debtId = debt.id || index; 

                    

                    if (debt.isPaidOff) {

                        return (

                            <Card key={debtId} element="earth" className="relative group border-yellow-500/50 bg-gradient-to-br from-yellow-900/10 to-slate-900">

                                <div className="absolute top-4 right-4 text-yellow-500 animate-pulse">

                                    <Trophy className="w-8 h-8" />

                                </div>

                                <div className="p-4 flex flex-col items-center text-center">

                                    <h3 className="text-2xl font-bold text-white line-through decoration-yellow-500">{debt.name}</h3>

                                    <div className="bg-yellow-500/20 text-yellow-400 font-bold px-4 py-2 rounded-lg border border-yellow-500/50 mt-4 mb-4 transform -rotate-2">

                                        LIABILITY EXTINGUISHED

                                    </div>

                                    <p className="text-xs text-slate-400 max-w-[200px]">

                                        Great job! You have cleared this debt. This victory card remains here as proof of your progress.

                                    </p>

                                    

                                    <div className="flex gap-4 mt-6">

                                        <button onClick={() => handleResurrect(debt)} className="text-xs text-slate-500 hover:text-white flex items-center gap-1 transition-colors">

                                            <RotateCcw className="w-3 h-3" /> Undo (Resurrect)

                                        </button>

                                        <button onClick={() => actions.removeArrayItem('debts', debtId)} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1 transition-colors">

                                            <Trash2 className="w-3 h-3" /> Remove Forever

                                        </button>

                                    </div>

                                </div>

                            </Card>

                        );

                    }



                    return (

                        <Card key={debtId} element="fire" className="relative group">

                             <div className="absolute -top-3 left-4 bg-orange-500 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded shadow-lg z-10 border border-orange-400">Priority #{index + 1}</div>

                             

                             <div className="flex justify-between items-start mb-4 pt-2">

                                <div className="flex-1 mr-4">

                                    <input value={debt.name || ""} onChange={(e) => actions.updateArrayItem('debts', debtId, 'name', e.target.value)} className="bg-transparent text-lg font-bold text-white outline-none border-b border-transparent focus:border-orange-500 w-full" placeholder="Debt Name" />

                                    <select value={debt.type || 'Other'} onChange={(e) => actions.updateArrayItem('debts', debtId, 'type', e.target.value)} className="bg-transparent text-xs text-slate-500 outline-none mt-1 hover:text-orange-400 cursor-pointer">

                                        {(LIABILITY_CATEGORIES || ['Other']).map(c => <option key={c} value={c} className="bg-slate-900 text-slate-300">{c}</option>)}

                                    </select>

                                </div>

                                <button onClick={() => handleExtinguish(debt)} className="p-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded hover:bg-emerald-500 hover:text-slate-900 transition-all" title="Mark as Paid Off">

                                    <CheckCircle className="w-5 h-5" />

                                </button>

                             </div>



                             <div className="grid grid-cols-3 gap-3 mb-4">

                                <InputGroup label="Balance" value={debt.balance} onChange={(v) => actions.updateArrayItem('debts', debtId, 'balance', v)} prefix="$" element="fire" />

                                <InputGroup label="APR %" value={debt.rate} onChange={(v) => actions.updateArrayItem('debts', debtId, 'rate', v)} element="fire" />

                                <InputGroup label="Min Pay" value={debt.minPayment} onChange={(v) => actions.updateArrayItem('debts', debtId, 'minPayment', v)} prefix="$" element="fire" />

                             </div>



                             {debt.type === 'Mortgage' && (

                                <div className="grid grid-cols-2 gap-3 mb-4 bg-slate-950/50 p-2 rounded border border-slate-800">

                                    <div className="flex flex-col">

                                        <label className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><Home className="w-3 h-3"/> Start Date</label>

                                        <input type="date" value={debt.startDate || ''} onChange={(e) => actions.updateArrayItem('debts', debtId, 'startDate', e.target.value)} className="bg-slate-900 border border-slate-700 rounded text-xs p-1 text-white outline-none" />

                                    </div>

                                    <div className="flex flex-col">

                                        <label className="text-[10px] text-slate-500 mb-1">Term (Years)</label>

                                        <input type="number" value={debt.originalTerm || 30} onChange={(e) => actions.updateArrayItem('debts', debtId, 'originalTerm', parseFloat(e.target.value))} className="bg-slate-900 border border-slate-700 rounded text-xs p-1 text-white outline-none" />

                                    </div>

                                </div>

                             )}



                             <div className="mb-4">

                                 <div className="flex items-center gap-2 mb-2">

                                     <input type="checkbox" checked={debt.hasPromo} onChange={(e) => actions.updateArrayItem('debts', debtId, 'hasPromo', e.target.checked)} className="accent-orange-500 bg-slate-900 border-slate-700 rounded" />

                                     <span className="text-xs text-slate-400">Promo / Deferred Interest?</span>

                                 </div>

                                 {debt.hasPromo && (

                                     <div className="grid grid-cols-2 gap-3 bg-slate-950/50 p-2 rounded border border-slate-800 animate-fade-in">

                                         <div className="flex flex-col">

                                             <label className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><Percent className="w-3 h-3"/> Promo Rate %</label>

                                             <input type="number" value={debt.promoRate || 0} onChange={(e) => actions.updateArrayItem('debts', debtId, 'promoRate', parseFloat(e.target.value))} className="bg-slate-900 border border-slate-700 rounded text-xs p-1 text-white outline-none" />

                                         </div>

                                         <div className="flex flex-col">

                                             <label className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3"/> Ends On</label>

                                             <input type="date" value={debt.promoEndDate || ''} onChange={(e) => actions.updateArrayItem('debts', debtId, 'promoEndDate', e.target.value)} className="bg-slate-900 border border-slate-700 rounded text-xs p-1 text-white outline-none" />

                                         </div>

                                     </div>

                                 )}

                             </div>



                             <div className="flex justify-between items-center text-xs bg-slate-950 p-2 rounded mb-2 border border-slate-800">

                                 <div>

                                     <span className="block text-[10px] text-slate-500 uppercase">Standard Payoff</span>

                                     <span className="text-slate-300 font-bold">{formatFutureDate(stdMonths)}</span>

                                 </div>

                                 {normalizedMonthlyExtra > 0 && (

                                     <div className="text-right">

                                         <span className="block text-[10px] text-orange-500 uppercase font-bold">Accelerated</span>

                                         <span className="text-white font-bold">{formatFutureDate(accMonths)}</span>

                                     </div>

                                 )}

                             </div>



                             <div className="h-24 bg-slate-900/50 rounded border border-slate-800 p-1 mb-2">

                                 {dataPoints.length > 1 ? (

                                     <ResponsiveContainer width="100%" height="100%">

                                         <AreaChart data={dataPoints}>

                                             <defs>

                                                 <linearGradient id={`grad-${debtId}`} x1="0" y1="0" x2="0" y2="1">

                                                     <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/><stop offset="95%" stopColor="#f97316" stopOpacity={0}/>

                                                 </linearGradient>

                                             </defs>

                                             <Tooltip contentStyle={{backgroundColor: '#0f172a', border: 'none', borderRadius: '8px', fontSize: '10px'}} itemStyle={{color: '#fff'}} />

                                             <Area type="monotone" dataKey="Standard" stroke="#94a3b8" fill="transparent" strokeWidth={1} dot={false} />

                                             <Area type="monotone" dataKey="Accelerated" stroke="#f97316" fill={`url(#grad-${debtId})`} strokeWidth={2} dot={false} />

                                         </AreaChart>

                                     </ResponsiveContainer>

                                 ) : <div className="flex items-center justify-center h-full text-xs text-slate-600">Enter balance, rate & min pay</div>}

                             </div>



                             <div className="flex items-center justify-between">

                                 <label className="flex items-center gap-2 cursor-pointer">

                                     <input type="checkbox" checked={debt.includeInNetWorth || false} onChange={(e) => actions.updateArrayItem('debts', debtId, 'includeInNetWorth', e.target.checked)} className="accent-orange-500 rounded bg-slate-900 border-slate-700" />

                                     <span className="text-[10px] text-slate-500">Include in Net Worth</span>

                                 </label>

                                 <button onClick={() => actions.removeArrayItem('debts', debtId)} className="opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3 text-slate-600 hover:text-red-400"/></button>

                             </div>

                        </Card>

                    );

                })}

                <button onClick={() => actions.addArrayItem('debts', {id: Date.now(), name: "New Liability", type: 'Credit Card', balance: 5000, rate: 20, minPayment: 150, extraPrincipal: 0, originalTerm: 10, includeInNetWorth: true, startDate: new Date().toISOString().split('T')[0]})} className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-800 rounded-xl hover:bg-orange-500/10 hover:border-orange-500/30 text-slate-500 hover:text-orange-400 transition-all min-h-[250px] group">

                    <Flame className="w-10 h-10 mb-3 group-hover:scale-110 transition-transform text-orange-500/50 group-hover:text-orange-500" />

                    <span className="font-bold">Add Liability</span>

                </button>

            </div>

        </div>

    );

};